import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell, Check, CheckCheck, Clock, Clipboard, Activity, UserPlus, AlertCircle, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + '/api' });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('fb_access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const TYPE_ICONS = {
  WELLNESS_REMINDER: Heart,
  RPE_REMINDER: Activity,
  SESSION_ASSIGNED: Clipboard,
  PRESCRIPTION_ASSIGNED: Clipboard,
  RTP_SESSION_ASSIGNED: AlertCircle,
  ASSIGNMENT_UPDATED: Clipboard,
  TEAM_MISSING_WELLNESS: Heart,
  TEAM_MISSING_RPE: Activity,
  INVITATION_CREATED: UserPlus,
  INVITATION_ACCEPTED: UserPlus,
  INVITATION_EXPIRED: Clock,
};

export function useNotifications(pollInterval = 30000) {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/notifications/unread-count');
      setCount(res.data.count);
    } catch { /* ignore */ }
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
      setCount(res.data.length);
    } catch { /* ignore */ }
    setLoading(false);
  }, [user]);

  const markAsRead = useCallback(async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications([]);
      setCount(0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, pollInterval);
    return () => clearInterval(interval);
  }, [fetchCount, pollInterval]);

  return { count, notifications, loading, fetchNotifications, markAsRead, markAllRead };
}

export function NotificationBell({ className = '', testId = 'notification-bell' }) {
  const { count, notifications, loading, fetchNotifications, markAsRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleToggle = () => {
    if (!open) fetchNotifications();
    setOpen(!open);
  };

  // Click on a notification: mark as read, close panel, navigate to target_route.
  // Routes are always relative to the app (no external URLs) — security gated by
  // the backend, which only ever stamps known internal routes.
  const handleNotificationClick = useCallback(async (n) => {
    const route = n.target_route;
    setOpen(false);
    await markAsRead(n.id);
    if (route && typeof route === 'string' && route.startsWith('/')) {
      navigate(route);
    }
  }, [markAsRead, navigate]);

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={handleToggle}
        aria-label={count > 0 ? `Notifications (${count} unread)` : 'Notifications'}
        className="relative inline-flex items-center justify-center w-11 h-11 text-zinc-400 hover:text-zinc-100 active:text-amber-400 transition-colors rounded-sm"
        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(245,158,11,0.2)' }}
        data-testid={testId}
      >
        <Bell className="w-5 h-5 pointer-events-none" />
        {count > 0 && (
          <span
            className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 bg-amber-500 text-zinc-950 text-[10px] font-bold rounded-full flex items-center justify-center pointer-events-none"
            data-testid="notification-badge"
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && createPortal(
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/30 sm:bg-transparent"
            onClick={() => setOpen(false)}
            data-testid="notification-overlay"
          />
          <div
            className="fixed inset-x-0 top-0 bottom-0 sm:inset-auto sm:right-4 sm:top-16 sm:w-80 sm:max-h-[80vh] sm:rounded-md bg-zinc-900 border border-zinc-700 shadow-2xl z-[70] overflow-hidden flex flex-col"
            style={{
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
            data-testid="notification-dropdown"
            role="dialog"
            aria-label="Notifications"
          >
            <div className="flex items-center justify-between px-3 py-3 border-b border-zinc-800 flex-shrink-0">
              <span className="text-sm font-semibold text-zinc-200">Notifications</span>
              <div className="flex items-center gap-2">
                {notifications.length > 0 && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    className="text-[11px] text-amber-400 hover:text-amber-300 flex items-center gap-1 px-2 py-1"
                    style={{ touchAction: 'manipulation' }}
                  >
                    <CheckCheck className="w-3 h-3" /> Mark all read
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close notifications"
                  className="sm:hidden inline-flex items-center justify-center w-9 h-9 text-zinc-400 hover:text-zinc-100 rounded-sm"
                  style={{ touchAction: 'manipulation' }}
                  data-testid="notification-close"
                >
                  <span className="text-lg leading-none">×</span>
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {loading && <div className="p-4 text-center text-zinc-500 text-xs">Loading...</div>}
              {!loading && notifications.length === 0 && (
                <div className="p-6 text-center text-zinc-500 text-xs">No new notifications</div>
              )}
              {notifications.map(n => {
                const Icon = TYPE_ICONS[n.type] || Bell;
                const hasRoute = !!(n.target_route && n.target_route.startsWith('/'));
                return (
                  <div
                    key={n.id}
                    role={hasRoute ? 'button' : undefined}
                    tabIndex={hasRoute ? 0 : undefined}
                    onClick={hasRoute ? () => handleNotificationClick(n) : undefined}
                    onKeyDown={hasRoute ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNotificationClick(n);
                      }
                    } : undefined}
                    className={`flex items-start gap-3 px-3 py-2.5 border-b border-zinc-800/50 transition-colors group ${
                      hasRoute ? 'cursor-pointer hover:bg-zinc-800/50 active:bg-zinc-800/70' : 'hover:bg-zinc-800/30'
                    }`}
                    style={{ touchAction: 'manipulation' }}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="mt-0.5 w-7 h-7 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-3.5 h-3.5 text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-zinc-200 truncate">{n.title}</div>
                      <div className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">{n.message}</div>
                      <div className="text-[10px] text-zinc-600 mt-1">
                        {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }}
                      className="mt-1 p-2 text-zinc-600 hover:text-amber-400 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                      style={{ touchAction: 'manipulation' }}
                      title="Mark as read"
                      data-testid={`mark-read-${n.id}`}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

export function NotificationList() {
  const { notifications, loading, fetchNotifications, markAsRead, markAllRead } = useNotifications();
  const navigate = useNavigate();

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleClick = useCallback(async (n) => {
    const route = n.target_route;
    await markAsRead(n.id);
    if (route && typeof route === 'string' && route.startsWith('/')) navigate(route);
  }, [markAsRead, navigate]);

  return (
    <div className="space-y-3" data-testid="notification-list">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-zinc-100">Notifications</h2>
        {notifications.length > 0 && (
          <Button variant="outline" size="sm" onClick={markAllRead} className="border-zinc-700 text-zinc-400 hover:text-amber-400 h-7 text-xs" data-testid="mark-all-read-btn">
            <CheckCheck className="w-3 h-3 mr-1" /> Mark all read
          </Button>
        )}
      </div>

      {loading && <div className="text-center text-zinc-500 text-sm py-8">Loading...</div>}
      {!loading && notifications.length === 0 && (
        <div className="text-center py-12 text-zinc-500 text-sm border border-zinc-800 rounded-md bg-zinc-950/50" data-testid="empty-notifications">
          <Bell className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
          No new notifications
        </div>
      )}
      {notifications.map(n => {
        const Icon = TYPE_ICONS[n.type] || Bell;
        const hasRoute = !!(n.target_route && n.target_route.startsWith('/'));
        return (
          <div
            key={n.id}
            role={hasRoute ? 'button' : undefined}
            tabIndex={hasRoute ? 0 : undefined}
            onClick={hasRoute ? () => handleClick(n) : undefined}
            onKeyDown={hasRoute ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(n); }
            } : undefined}
            className={`flex items-start gap-3 p-3 bg-zinc-900/50 border border-zinc-800 rounded-sm transition-colors group ${
              hasRoute ? 'cursor-pointer hover:border-amber-500/40' : 'hover:border-zinc-700'
            }`}
            data-testid={`notification-item-${n.id}`}
          >
            <div className="mt-0.5 w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-zinc-200">{n.title}</div>
              <div className="text-xs text-zinc-400 mt-0.5">{n.message}</div>
              <div className="text-[10px] text-zinc-600 mt-1">
                {new Date(n.created_at).toLocaleString()}
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }}
              className="mt-1 p-1.5 text-zinc-600 hover:text-amber-400 rounded hover:bg-zinc-800 transition-colors"
              title="Mark as read"
              data-testid={`mark-read-${n.id}`}
            >
              <Check className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
